import { IonCol, IonGrid, IonRow } from "@ionic/react";
import React, { FC, useEffect, useState } from "react";
import { API_URL } from "../../../actions/settings";
import { default as verified_savings } from "../../../AE2/static/icons/kpi_summary/Verified Savings.svg"
import { default as incentives_icon } from "../../../AE2/static/icons/kpi_summary/Incentives.svg"
import { default as buildings_tracked_icon } from "../../../AE2/static/icons/kpi_summary/Buildings Tracked.svg"
import { default as equiv_solar_panels_icon } from "../../../AE2/static/icons/kpi_summary/Solar Panel Equiv..svg"
import { default as avg_ghg_reduction_icon } from "../../../AE2/static/icons/kpi_summary/Avg. GHG Reduction.svg"
import { default as up_arrow_icon } from "../../../AE2/static/icons/kpi_summary/Up Arrow.svg"
import { default as building_placeholder } from "../../../AE2/static/icons/customer_page/Building Performance Overview/Building Photo Placeholder.svg"
import { default as co2_icon } from "../../../AE2/static/icons/customer_page/Environmental Impact/CO2.svg"
import { default as trees_icon } from "../../../AE2/static/icons/customer_page/Environmental Impact/Trees Planted.svg"
import { default as homes_icon } from "../../../AE2/static/icons/customer_page/Environmental Impact/Homes Powered.svg"
import { default as cars_icon } from "../../../AE2/static/icons/customer_page/Environmental Impact/Cars Off Road.svg"
import { default as download_icon } from "../../../AE2/static/icons/download-icon.svg"
import GoogleCustomersMapV2 from "../../extras/GoogleCustomersMapV2";
import WelcomeRow from "../StaffHomepage/WelcomeRow";
import EnergyAnalysisReportButton from "../CustomerPageComponents/EnergyAnalysisReportButton";
import CampusMeterSummaryReportButton from "../CustomerPageComponents/CampusMeterSummaryReportButton";
import LoadingSpinner from "../../extras/LoadingSpinner";
import "../StaffHomepage/SummaryRow.css";
import "./CustomerDashboard.css";
import {
    ACTIVITY_ICONS,
    ActivityItem,
    BuildingStatus,
    CustomerDashboardData,
    DashboardBuilding,
    EnvironmentalImpact as ImpactData,
    NeedsAttentionItem,
    REPORTS,
    STATUS_ICONS,
} from "./customerDashboardData";

interface IProps {
    customer: any
    customerId: string
}

async function getDashboard(token: string, customerId: string) {
    return fetch(`${API_URL}/customer/${customerId}/dashboard`, {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: token,
        },
    }).then((res) => res.ok ? res.json() : null).catch((error) => {
        console.error(error)
        return null
    })
}

const compact = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 2 })
const full = new Intl.NumberFormat('en-US')
const dateFormatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' })

const formatDate = (value: string) => {
    const date = new Date(value)
    return isNaN(date.getTime()) ? '' : dateFormatter.format(date)
}

const KPI = ({ icon, value, title, subtitle, trend, trendNegative }: { icon: string, value: string, title: string, subtitle?: string, trend?: string, trendNegative?: boolean }) =>
    <IonCol sizeXl="2" size="6" sizeMd="4">
        <div className="kpi-container grey-border-2pt" style={{ display: 'flex' }}>
            <div className="kpi-icon-container">
                <img src={icon} alt={`${title} icon`} />
            </div>
            <div>
                <div className="kpi-container-value">{value}</div>
                <div className="kpi-container-title">{title}</div>
                {subtitle ? <div className="kpi-container-subtitle">{subtitle}</div> : null}
                {trend ? <div className={`kpi-container-trend${trendNegative ? ' kpi-value-negative' : ''}`}>
                    <img className={trendNegative ? 'kpi-icon-negative' : undefined} src={up_arrow_icon} alt={trendNegative ? 'trend down icon' : 'trend up icon'} />{trend}
                </div> : null}
            </div>
        </div>
    </IonCol>

const BuildingPerformance: FC<{ buildings: DashboardBuilding[] }> = ({ buildings }) =>
    <div className="cd-card cd-building-card">
        <div className="cd-card-header"><div className="cd-card-title">Building Performance Overview</div></div>
        <div className="cd-table-scroll">
        <table className="cd-table">
            <thead>
                <tr><th>Building</th>
                <th><div>Savings</div> <div> To Date</div></th>
                <th>
                    <div title="Energy Star Score Status">ES Score</div>
                    <div>
                        Status
                    </div></th></tr>
            </thead>
            <tbody>
                {buildings.map((b) =>
                    <tr key={b.uuid}>
                        <td>
                            <a className="cd-building-cell" style={{ textDecoration: 'none', color: 'inherit' }} href={`/?id=${b.uuid}&sec=buildings`}>
                                <img className="cd-building-photo" src={building_placeholder} alt="" />
                                <div>
                                    <div className="cd-building-name">{b.building_name}</div>
                                    <div className="cd-building-type">{b.type}</div>
                                </div>
                            </a>
                        </td>
                        <td style={{ fontWeight: 500 }}>{b.savings === null ? 'N/A' : `$${compact.format(b.savings)}`}</td>
                        <td><img className="cd-status-icon" src={STATUS_ICONS[b.status]} alt={b.status} /></td>
                    </tr>
                )}
                {buildings.length === 0 ? <tr><td colSpan={3}>There are no buildings at this moment.</td></tr> : null}
            </tbody>
        </table>
        </div>
    </div>

const RecentActivity: FC<{ items: ActivityItem[] }> = ({ items }) =>
    <div className="cd-card" style={{ marginBottom: '1rem' }}>
        <div className="cd-card-header">
            <div className="cd-card-title">Recent Activity</div>
        </div>
        {items.map(a =>
            <div className="cd-activity-row" key={a.id}>
                {ACTIVITY_ICONS[a.type] ? <img className="cd-activity-icon" src={ACTIVITY_ICONS[a.type]} alt="" /> : null}
                <div className="cd-activity-text">
                    {a.title}
                    {a.detail ? <div className="cd-activity-sub">{a.detail}</div> : null}
                </div>
                <div className="cd-activity-date">{formatDate(a.date)}</div>
            </div>
        )}
        {items.length === 0 ? <div className="cd-activity-sub">No recent activity.</div> : null}
    </div>

const NeedsAttention: FC<{ items: NeedsAttentionItem[] }> = ({ items }) =>
    <div className="cd-card" style={{ marginBottom: '1rem' }}>
        <div className="cd-card-header">
            <div className="cd-card-title">Needs Attention</div>
        </div>
        {items.map(a =>
            <a className="cd-activity-row" key={`${a.id}-${a.uuid}`} style={{ textDecoration: 'none', color: 'inherit' }} href={`/?id=${a.uuid}&sec=buildings`}>
                <div className="cd-activity-text">
                    {a.building_name}
                    <div className="cd-activity-sub">{a.title}</div>
                </div>
                <div className="cd-activity-date">{a.detail}</div>
            </a>
        )}
    </div>

const Reports: FC<{ customerId: string, customerName?: string }> =({ customerId, customerName }) =>
    <div className="cd-card">
        <div className="cd-card-header">
            <div className="cd-card-title">Reports</div>
        </div>
        <EnergyAnalysisReportButton uuid={customerId} row />
        <CampusMeterSummaryReportButton uuid={customerId} name={customerName} row />
        {/* {REPORTS.map(r =>
            <div className="cd-report-row" key={r.id}>
                <img src={r.icon} alt="" />
                <div className="cd-report-title">{r.title}</div>
                <button className="cd-report-download" aria-label={`Download ${r.title}`}>
                    <img src={download_icon} alt="" style={{ width: '20px', height: '20px' }} />
                </button>
            </div>
        )} */}
    </div>

const EnvironmentalImpact: FC<{ impact: ImpactData }> = ({ impact }) => {
    const items = [
        { id: 'co2', icon: co2_icon, value: impact.co2_tons, label: 'Tons of CO₂ Avoided' },
        { id: 'trees', icon: trees_icon, value: impact.trees, label: 'Trees Planted' },
        { id: 'homes', icon: homes_icon, value: impact.homes, label: 'Homes Powered/Year' },
        { id: 'cars', icon: cars_icon, value: impact.cars, label: 'Cars Off the Road' },
    ]
    return <IonRow>
        {items.map(i =>
            <IonCol key={i.id} size="12" sizeMd="6" sizeXl="3">
                <div className="cd-card cd-impact">
                    <img src={i.icon} alt="" />
                    <div>
                        <div className="cd-impact-value">{full.format(i.value)}</div>
                        <div className="cd-impact-label">{i.label}</div>
                    </div>
                </div>
            </IonCol>
        )}
    </IonRow>
}

const countStatus = (buildings: DashboardBuilding[], status: BuildingStatus) =>
    buildings.filter(b => b.status === status).length

const CustomerDashboard: FC<IProps> = ({ customer, customerId }) => {
    const [data, setData] = useState<CustomerDashboardData | null>(null)
    const [loading, setLoading] = useState(true)
    const [refreshDate, setRefreshDate] = useState(new Date())

    useEffect(() => {
        const handleData = async () => {
            const token = `Token ${(localStorage.getItem("token") || '').slice(1, -1)}`
            const res = await getDashboard(token, customerId)            
            if (res?.data) {
                setData(res.data)
                setRefreshDate(new Date())
            }
            setLoading(false)
        }
        handleData()
    }, [customerId])

    if (loading) {
        return <LoadingSpinner />
    }

    if (!data) {
        return <div className="cd-card" style={{ margin: '1rem' }}>We couldn't load your dashboard right now. Please try again later.</div>
    }

    const { summary, buildings } = data

    const subtitle = <IonRow className="text-subtitle">
        <div>{customer?.cust_address} {customer?.cust_city}, {customer?.cust_state}, {customer?.cust_zipcode}</div>
        <div style={{marginRight:'1rem',marginLeft:'1rem',fontWeight:'bold'}} >&#183;</div>
        <div>{full.format(summary.totalSqFt)} ft<sup>2</sup></div>
        <div style={{marginRight:'1rem',marginLeft:'1rem',fontWeight:'bold'}} >&#183;</div>
        <div title="Technical Solutions Area">TSA: {customer?.technical_solutions_area?.name}</div>
    </IonRow>

    return <IonGrid>
        <WelcomeRow date={refreshDate} customerName={customer?.cust_name} customerId={customerId} subtitle={subtitle}/>
        <IonRow>
            <KPI icon={verified_savings} value={`$${compact.format(summary.verifiedSavings.total_savings)}`} title="Verified Savings" subtitle="Total to Date"
                trend={summary.verifiedSavings.deltaThisMonth ? `$${compact.format(summary.verifiedSavings.deltaThisMonth)} this month` : undefined}
                trendNegative={summary.verifiedSavings.deltaThisMonth < 0} />
            <KPI icon={incentives_icon} value={`$${compact.format(summary.incentives.total)}`} title="Incentives" subtitle="Total to Date" />
            <KPI icon={buildings_tracked_icon} value={full.format(summary.buildingsTracked.value)} title="Buildings Tracked" />
            <KPI icon={equiv_solar_panels_icon} value={compact.format(summary.solarPanels.total)} title="Solar Panel Equiv." subtitle="Total to Date"
                trend={summary.solarPanels.deltaThisMonth ? `${compact.format(summary.solarPanels.deltaThisMonth)} this month` : undefined}
                trendNegative={summary.solarPanels.deltaThisMonth < 0} />
            <KPI icon={avg_ghg_reduction_icon} value={`${summary.ghgReduction.percent_reduction}%`} title="Avg. GHG Reduction" subtitle="Total to Date"
                trend={summary.ghgReduction.deltaThisMonth ? `${summary.ghgReduction.deltaThisMonth}% this month` : undefined}
                trendNegative={summary.ghgReduction.deltaThisMonth < 0} />
        </IonRow>
        <IonRow>
            <IonCol size="12" sizeLg="6" sizeXl="4" className="cd-building-col">
                <BuildingPerformance buildings={buildings} />
            </IonCol>
            <IonCol size="12" sizeLg="6" sizeXl="4">
                <GoogleCustomersMapV2 title="Portfolio Location Map" apiUrl={`${API_URL}/customer/${customerId}/dashboard/map_buildings`} />
                <div className="cd-map-legend">
                    <span><span className="cd-dot cd-dot-good" />{countStatus(buildings, 'Good')} Excellent/Good</span>
                    <span><span className="cd-dot cd-dot-monitor" />{countStatus(buildings, 'Monitor')} Monitor</span>
                    <span><span className="cd-dot cd-dot-attention" />{countStatus(buildings, 'Needs Attention')} Needs Attention</span>
                </div>
            </IonCol>
            <IonCol size="12" sizeXl="4">
                {data.needsAttention && data.needsAttention.length > 0 ? <NeedsAttention items={data.needsAttention} /> : null}
                <RecentActivity items={data.recentActivity} />
                <Reports customerId={customerId} customerName={customer?.cust_name} />
            </IonCol>
        </IonRow>
        <EnvironmentalImpact impact={data.environmentalImpact} />
    </IonGrid>
};

export default CustomerDashboard;
